/**
 * OwnerDetailModal
 * Read-only side-sheet showing all relevant information for a given owner:
 *  - User profile data
 *  - Associated company
 *  - Branches belonging to that company
 */

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  User as UserIcon,
  Building2,
  Briefcase,
  Mail,
  AtSign,
  CalendarDays,
  Store,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import { usersService, type User } from "@/services/usersService";
import { getCompanyById, type Company } from "@/services/companiesService";
import { getBranches, type Branch } from "@/services/branchesService";
import UserAvatar from "@/components/UserAvatar";

interface OwnerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  owner: User | null;
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-white/40">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-white/40 mb-0.5">{label}</p>
        <p className="text-sm text-white break-words">{value || <span className="text-white/30 italic">Sin dato</span>}</p>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
      {children}
    </p>
  );
}

export default function OwnerDetailModal({
  isOpen,
  onClose,
  owner,
}: OwnerDetailModalProps) {
  const [company, setCompany] = useState<Company | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !owner) {
      setCompany(null);
      setBranches([]);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const [companyData, branchesData] = await Promise.all([
          owner.company_id ? getCompanyById(owner.company_id) : Promise.resolve(null),
          owner.company_id ? getBranches(owner.company_id) : Promise.resolve([]),
        ]);
        setCompany(companyData);
        setBranches(branchesData);
      } catch (err) {
        console.error("Error loading owner details:", err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [isOpen, owner]);

  if (!owner) return null;

  const fullName = usersService.getFullName(owner);
  const initials = usersService.getInitials(owner);
  const createdAt = owner.created_at
    ? new Date(owner.created_at).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header ─────────────────────────────────────── */}
        <SheetHeader className="flex-shrink-0 px-6 pb-4 border-b border-white/10" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}>
          <div className="flex items-center gap-4">
            <UserAvatar initials={initials} role="owner" size="xl" />
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {fullName}
              </SheetTitle>
              <p className="text-sm text-white/50 mt-0.5">@{owner.username}</p>
              <div className="mt-2">
                <Badge
                  className={
                    owner.is_active
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                      : "bg-red-500/20 text-red-400 border-red-500/30"
                  }
                >
                  {owner.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Scrollable body ─────────────────────────────── */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Usuario ─────────────────────────────────── */}
            <section className="space-y-3">
              <SectionTitle>
                <UserIcon className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Información del usuario
              </SectionTitle>

              <InfoRow
                icon={<AtSign className="h-4 w-4" />}
                label="Usuario"
                value={owner.username}
              />
              <InfoRow
                icon={<Mail className="h-4 w-4" />}
                label="Correo electrónico"
                value={owner.email}
              />
              <InfoRow
                icon={<UserIcon className="h-4 w-4" />}
                label="Nombre completo"
                value={fullName}
              />
              <InfoRow
                icon={<CalendarDays className="h-4 w-4" />}
                label="Registrado el"
                value={createdAt}
              />
            </section>

            <Separator className="bg-white/10" />

            {/* Empresa ─────────────────────────────────── */}
            <section className="space-y-3">
              <SectionTitle>
                <Building2 className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Empresa
              </SectionTitle>

              {isLoading ? (
                <Skeleton className="h-16 w-full rounded-xl bg-white/10" />
              ) : company ? (
                <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10 space-y-2">
                  <div className="flex items-center gap-3">
                    <UserAvatar role="company" size="sm" icon={<Building2 className="h-4 w-4" />} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{company.name}</p>
                      <Badge
                        className={
                          company.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mt-1"
                            : "bg-red-500/20 text-red-400 border-red-500/30 mt-1"
                        }
                      >
                        {company.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white/30 italic">Sin empresa asignada</p>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Sucursales ──────────────────────────────── */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <SectionTitle>
                  <Briefcase className="inline-block h-3 w-3 mr-1 mb-0.5" />
                  Sucursales
                </SectionTitle>
                {!isLoading && branches.length > 0 && (
                  <span className="text-xs text-white/40 -mt-3">{branches.length} registradas</span>
                )}
              </div>

              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <Skeleton key={i} className="h-14 w-full rounded-xl bg-white/10" />
                  ))}
                </div>
              ) : branches.length === 0 ? (
                <p className="text-sm text-white/30 italic">Sin sucursales registradas</p>
              ) : (
                <div className="space-y-2">
                  {branches.map((branch) => (
                    <div
                      key={branch.id}
                      className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 border border-white/10"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                          <Store className="h-4 w-4 text-amber-400" />
                        </div>
                        <p className="text-sm text-white font-medium truncate">
                          {branch.name}
                        </p>
                      </div>
                      <Badge
                        className={
                          branch.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 ml-2 flex-shrink-0"
                            : "bg-red-500/20 text-red-400 border-red-500/30 ml-2 flex-shrink-0"
                        }
                      >
                        {branch.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Configuración del panel ──────────────────── */}
            <section className="space-y-3">
              <SectionTitle>
                <ShieldCheck className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Configuración del panel
              </SectionTitle>
              {(() => {
                const mc = (owner.modules_config ?? {}) as Record<string, any>;
                const turnosEnabled = !!mc["financial-summary"]?.turnos;
                return (
                  <div
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 ${
                      turnosEnabled
                        ? "bg-white/5 border-white/10"
                        : "bg-transparent border-white/5 opacity-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-white/40" />
                      <div>
                        <p className="text-sm font-medium text-white">Módulo de turnos</p>
                        <p className="text-xs text-white/30 mt-0.5">
                          Reporte de turnos en resumen financiero
                        </p>
                      </div>
                    </div>
                    {turnosEnabled ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-white/20 flex-shrink-0" />
                    )}
                  </div>
                );
              })()}
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
